/**
 * Inversion Acknowledgment Service
 *
 * Reads and writes `priority_inversion_acknowledgments` rows.
 * Used by the priority list governance flow to gate publication on
 * explicit acknowledgment of all prioritization inversions.
 *
 * Canon: DOCS_V4/00-canon/01-terminology-and-glossary_v3.md
 *        "Prioritization Inversion"
 * Build Contract: §2.3a (authority transition via DB), §2.3 (schema + write + read + UI)
 */

import { getSafeSupabaseClient, getCurrentWorkspaceId } from './supabaseSync';
import {
  type InversionAcknowledgment,
  type InversionAcknowledgmentRow,
  type PrioritizationInversion,
  rowToInversionAcknowledgment,
} from './prioritizationInversionDetector';

const TABLE = 'priority_inversion_acknowledgments';

// ============================================================================
// Read
// ============================================================================

/**
 * Load all acknowledgments for a given priority list.
 */
export async function loadInversionAcknowledgments(
  prioritySetId: string,
): Promise<InversionAcknowledgment[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('priority_set_id', prioritySetId)
    .order('acknowledged_at', { ascending: true });

  if (error) {
    console.error('[InversionAcknowledgmentService] Failed to load acknowledgments:', error);
    return [];
  }

  return (data ?? []).map((row) => rowToInversionAcknowledgment(row as InversionAcknowledgmentRow));
}

/**
 * Load all hard sequence edges (dependency + prerequisite) for items in a ranked set.
 * Used by the publish gate to detect inversions without requiring the
 * full relationship store to be hydrated.
 *
 * Returns edges in stored form; callers should normalize via normalizeSequencePair /
 * detectPrioritizationInversions rather than assuming dependency source/target polarity.
 */
export async function loadHardDependencyEdgesForItems(
  itemIds: string[],
): Promise<Array<{ id: string; sourceItemId: string; targetItemId: string; type: 'dependency' | 'prerequisite'; subtype?: string | null }>> {
  if (itemIds.length === 0) return [];

  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  const select = 'id, source_item_id, target_item_id, relationship_type, subtype';
  const base = () =>
    supabase
      .from('item_relationship_edges')
      .select(select)
      .in('relationship_type', ['dependency', 'prerequisite'])
      .not('status', 'in', '("dissolved","dismissed")');

  const [asSource, asTarget] = await Promise.all([
    base().in('source_item_id', itemIds),
    base().in('target_item_id', itemIds),
  ]);

  if (asSource.error) {
    console.error('[InversionAcknowledgmentService] Failed to load edges (source):', asSource.error);
  }
  if (asTarget.error) {
    console.error('[InversionAcknowledgmentService] Failed to load edges (target):', asTarget.error);
  }

  const byId = new Map<string, {
    id: string;
    sourceItemId: string;
    targetItemId: string;
    type: 'dependency' | 'prerequisite';
    subtype?: string | null;
  }>();

  for (const row of [...(asSource.data ?? []), ...(asTarget.data ?? [])]) {
    const type = row.relationship_type as string;
    const subtype = (row.subtype as string | null) ?? null;
    if (type === 'dependency' && subtype !== 'hard' && subtype !== 'gate') continue;
    if (type !== 'dependency' && type !== 'prerequisite') continue;
    byId.set(row.id as string, {
      id: row.id as string,
      sourceItemId: row.source_item_id as string,
      targetItemId: row.target_item_id as string,
      type: type as 'dependency' | 'prerequisite',
      subtype,
    });
  }

  return [...byId.values()];
}

// ============================================================================
// Write
// ============================================================================

export interface AcknowledgeInversionArgs {
  prioritySetId: string;
  inversion: PrioritizationInversion;
  acknowledgedBy: string;
  rationale: string;
}

export interface AcknowledgeInversionResult {
  success: boolean;
  acknowledgment?: InversionAcknowledgment;
  error?: string;
}

/**
 * Persist an inversion acknowledgment.
 *
 * Build Contract §2.3a: authority transition — this is a pre-publication
 * governance act that gates a state transition.
 */
export async function acknowledgeInversion(
  args: AcknowledgeInversionArgs,
): Promise<AcknowledgeInversionResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase client not available' };
  }

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    return { success: false, error: 'No active workspace' };
  }

  const { inversion } = args;

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(
      {
        workspace_id: workspaceId,
        priority_set_id: args.prioritySetId,
        higher_ranked_item_id: inversion.higherRankedItemId,
        lower_ranked_item_id: inversion.lowerRankedItemId,
        higher_rank: inversion.higherRank,
        lower_rank: inversion.lowerRank,
        dependency_edge_id: inversion.dependencyEdgeId ?? null,
        acknowledged_by: args.acknowledgedBy,
        acknowledged_at: new Date().toISOString(),
        rationale: args.rationale,
      },
      { onConflict: 'priority_set_id,higher_ranked_item_id,lower_ranked_item_id' },
    )
    .select()
    .single();

  if (error) {
    console.error('[InversionAcknowledgmentService] Failed to acknowledge inversion:', error);
    return { success: false, error: error.message };
  }

  return {
    success: true,
    acknowledgment: rowToInversionAcknowledgment(data as InversionAcknowledgmentRow),
  };
}
